import React, { useRef, useState } from "react";
import cn from "classnames";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import styles from "./CreateOrder.module.sass";
import Card from "../../components/Card";
import Icon from "../../components/Icon";
import TextInput from "../../components/TextInput";
import { CREATE_ORDER, DELETE_ORDER } from "../../API/CreateOrder";
import CustomToast from "../../components/CustomToast";
import OrdersTable from "./OrdersTable/index";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Modal from "../../components/Modal";
import { Divider } from "antd";

const validationSchema = Yup.object().shape({
  order_number: Yup.string()
    .required("Order number is required")
    .max(100, "Maximum 100 characters allowed"),
});

const initialValues = {
  order_number: "",
};

const CreateOrder = ({ className }) => {
  const showToast = CustomToast();
  const [orderImportTrigger, setOrderImportTrigger] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const triggerOrdersRefresh = () => {
    setOrderImportTrigger((prev) => prev + 1);
  };

  const handleSave = async (values, { setSubmitting, resetForm }) => {
    try {
      const data = await CREATE_ORDER(parseInt(values.order_number));
      showToast("Order created successfully!", "success");
      resetForm();
      triggerOrdersRefresh();
    } catch (error) {
      showToast("Failed to create order: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const batchCreateOrders = async (orderNumbers) => {
    try {
      const validOrderNumbers = orderNumbers
        .map((order) => parseInt(order["Order Number"]))
        .filter((orderNumber) => !isNaN(orderNumber));

      if (validOrderNumbers.length === 0) {
        showToast("No valid order numbers found.", "error");
        return;
      }

      await CREATE_ORDER(validOrderNumbers);

      showToast(
        `${validOrderNumbers.length} orders imported successfully!`,
        "success"
      );
      triggerOrdersRefresh();
    } catch (error) {
      showToast(`Failed to create orders: ${error.message}`, "error");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (extension === "csv" || extension === "txt") {
        handleCSVorTXTImport(file);
      } else if (extension === "xlsx") {
        handleExcelImport(file);
      } else {
        showToast(
          "Unsupported file format. Please upload CSV, XLSX, or TXT files.",
          "error"
        );
      }
    }
  };

  const handleCSVorTXTImport = async (file) => {
    try {
      Papa.parse(file, {
        header: true,
        complete: async (results) => {
          const orders = results.data.filter((order) => order["Order Number"]);
          await batchCreateOrders(orders);
        },
        error: (error) => {
          showToast("Failed to parse CSV/TXT: " + error.message, "error");
        },
      });
    } catch (error) {
      console.error("Unexpected error during import:", error);
      showToast("An unexpected error occurred: " + error.message, "error");
    }
  };

  const handleExcelImport = async (file) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        const orders = data.filter((order) => order["Order Number"]);
        await batchCreateOrders(orders);
      } catch (error) {
        console.error("Error processing Excel file:", error);
        showToast(`Failed to import Excel file: ${error.message}`, "error");
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleModalOpen = () => {
    if (!selectedOrderId || selectedOrderId.length === 0) {
      showToast("Please select at least one order to delete", "warning");
      return;
    }
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleSelectedOrdersChange = (orderIds) => {
      setSelectedOrderId(orderIds);
    };
 const handleDeleteConfirm = async () => {
  if (!selectedOrderId || selectedOrderId.length === 0) return;
  try {
    setIsDeleting(true);
    await DELETE_ORDER(selectedOrderId);
    showToast(`Orders deleted successfully`, "success");
    setIsModalVisible(false);
    triggerOrdersRefresh();
    setSelectedOrderId([]);
  } catch (error) {
    showToast(`Failed to delete orders: ${error.message}`, "error");
  } finally {
    setIsDeleting(false);
  }
};
  return (
    <>
      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <Card
                className={cn("mb-[10px]", styles.card, className)}
                title="Order"
                classTitle="title-blue"
                head={
                  <Link
                    className={cn("button-stroke button-small", styles.button)}
                    to="/orders"
                  >
                    <Icon name="arrow-left" size="24" />
                    <span>Back</span>
                  </Link>
                }
              >
                <div className={styles.description}>
                  <div className={styles.field_container}>
                    <Field name="order_number">
                      {({ field }) => (
                        <TextInput
                          {...field}
                          className={styles.field}
                          label="Order Number"
                          name="order_number"
                          type="text"
                          tooltip="Maximum 100 characters. No HTML or emoji allowed"
                          error={touched.order_number && errors.order_number}
                        />
                      )}
                    </Field>
                    <div className={styles.button_container}>
                      <button
                        type="button"
                        className={cn("button-stroke button-small")}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Icon name="upload" size="24" />
                        Import Orders
                      </button>
                      <input
                        type="file"
                        accept=".csv, .xlsx, .txt"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <button
                        type="submit"
                        className={cn("button", styles.button)}
                        disabled={isSubmitting}
                      >
                        <Icon name="add" size="24" /> Add Order
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
              <Card className={cn("mb-[85px]", styles.card)}>
                <div className="flex justify-between items-center mb-12">
                  <div className={cn("title-yellow", styles.subtitle)}>
                    Order
                  </div>
                  <div className={styles.btns}>
                    <button type="button" className={cn("button-stroke-red", styles.button)} onClick={handleModalOpen} >
                    <Icon name="trash" size="24" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
                <OrdersTable key={orderImportTrigger}  onSelectedOrdersChange={handleSelectedOrdersChange}/>
              </Card>
            </Form>
          )}
        </Formik>

        <Modal  visible={isModalVisible}  onClose={handleModalClose}>
          <div className={styles.details}>
            <div className={cn("title-yellow", styles.title)}>
               Delete Orders
            </div>
            <div className={styles.roww}>
              <div className={styles.coll}>
                <Divider />
                <p className="text-[#6F767E]">
                This action cannot be undone. The order and its details will be permanently deleted.
                </p>

                <div className={cn(styles.btns)}>
                <button
                  type="button"
                  className={cn("button-stroke pl-5 pr-5", styles.button)}
                  onClick={handleModalClose}
                >
                No, Go back
                </button>
                  <button
                    type="button"
                    className={cn("button", styles.button)}
                    onClick={handleDeleteConfirm}
                  >
                    Yes, Delete them
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default CreateOrder;
